import React from 'react';
import {
  YesNoQuestion,
  QuestionAnswer,
  QuestionInterface,
  NumberQuestion
} from './questions/QuestionTypes';

// We need to create new question types for Number inputs
interface NumberQuestionProps {
  id: string;
  question: string;
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  required?: boolean;
  error?: string;
}

const NumberQuestionComponent: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      className="w-32 input"
      min="0"
      value={value.answer || ''}
      onChange={(e) => onChange({ ...value, answer: e.target.value })}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

// Yes/No with explanation for No component for question 17a
const YesNoExplanationForNoQuestion: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex mb-2 space-x-4">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'No' && (
      <div>
        <label className="text-sm label">Please explain why line not moving:</label>
        <textarea
          className="w-full h-20 resize-none input"
          placeholder="Explain why the line is not moving..."
          value={value.details || ''}
          onChange={(e) => onChange({ ...value, details: e.target.value })}
        />
      </div>
    )}
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

interface Question2Data {
  [questionId: string]: QuestionAnswer;
}

interface Step3Question2Props {
  data: Question2Data;
  onChange: (data: Question2Data) => void;
  errors: Record<string, string>;
  selectedTimeline?: string;
  questionNumber?: number;
  questionTitle?: string;
  questionDescription?: string;
  currentSection?: number;
  totalSections?: number;
}

// Timeline-based questions for Question 7 (Operations and Accessibility)
const TIMELINE_QUESTIONS: Record<string, QuestionInterface[]> = {
  morning: [
    // 7a
    { id: '7a', question: 'The voting queue/line was moving along during the Morning hours (6:00am to 12:00pm)?', type: 'yes_no_explanation_for_no', required: true },
    // 7b
    { id: '7b', question: 'The Polling Centre was spacious and adequate for voters\' movement and voting process', type: 'yes_no', required: true },
    // 7c
    { id: '7c', question: 'The polling Centre was shaded and protected from the sun and rain', type: 'yes_no', required: true },
    // 7d
    { id: '7d', question: 'The polling Centre implemented measures with respect to protecting voters from any health outbreaks at the time', type: 'yes_no', required: true },
  ],
  afternoon: [
    // 17a
    { id: '17a', question: 'The voting queue/line was moving along during the Afternoon hours (12:00pm to 04:00pm)?', type: 'yes_no_explanation_for_no', required: true },
    // 17b
    { id: '17b', question: 'The Polling Centre was spacious and adequate for voters\' movement and voting process', type: 'yes_no', required: true },
    // 17c
    { id: '17c', question: 'The polling Centre was shaded and protected from the sun and rain', type: 'yes_no', required: true },
    // 17d
    { id: '17d', question: 'The polling Centre implemented measures with respect to protecting voters from any health outbreaks at the time', type: 'yes_no', required: true },
  ],
  vote_count: [
    // No facility questions for vote count timeline
  ]
};

const QUESTION_COMPONENTS = {
  'yes_no': YesNoQuestion,
  'number': NumberQuestionComponent,
  'yes_no_explanation_for_no': YesNoExplanationForNoQuestion,
};

const Step3Question2: React.FC<Step3Question2Props> = ({
  data,
  onChange,
  errors,
  selectedTimeline,
  questionNumber = 8,
  questionTitle = "Voting Environment",
  questionDescription = "This section covers the voting environment including polling station security, voter assistance, and polling station management.",
  currentSection = 8,
  totalSections = 11
}) => {
  const updateQuestion = (questionId: string, value: QuestionAnswer) => {
    onChange({
      ...data,
      [questionId]: value
    });
  };

  // Get questions for the selected timeline with conditional filtering
  const getQuestionsForTimeline = () => {
    if (!selectedTimeline || !TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS]) {
      return [];
    }

    const allQuestions = TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS];

    // Filter out conditional questions that shouldn't be shown
    return allQuestions.filter(question => {
      if (!question.conditional) return true;

      const dependentAnswer = data?.[question.conditional.dependsOn]?.answer;
      return dependentAnswer === question.conditional.showWhen;
    });
  };

  const questions = getQuestionsForTimeline();

  const getCompletionStatus = () => {
    const completed = questions.filter(q => data?.[q.id]?.answer).length;
    return { completed, total: questions.length };
  };

  const { completed, total } = getCompletionStatus();

  return (
    <div className="space-y-6">
      {/* Section Header with Step Number and Progress */}
      <div className="p-4 border border-blue-100 rounded-lg bg-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Question {questionNumber}: {questionTitle}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {questionDescription}
            </p>
          </div>
          <div className="text-right">
            <span className="px-3 py-1 text-sm font-medium text-blue-800 bg-blue-100 rounded-full">
              Section {currentSection}/{totalSections}
            </span>
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 rounded-full"
            style={{ width: `${(currentSection / totalSections) * 100}%` }}
          ></div>
        </div>
      </div>

      <div>
        <p className="mb-4 text-gray-600">
          Timeline: {selectedTimeline ? selectedTimeline.charAt(0).toUpperCase() + selectedTimeline.slice(1).replace('_', ' ') : 'No timeline selected'}
        </p>
        {!selectedTimeline && (
          <div className="p-4 mb-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-yellow-800">Please select a timeline in Step 1 to see relevant questions for Question {questionNumber}.</p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="p-3 mb-6 border border-green-200 rounded-lg bg-green-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-green-900">Question {questionNumber} Progress</span>
            <span className="text-sm text-green-800">{completed}/{total} completed</span>
          </div>
          <div className="h-2 mt-2 bg-green-200 rounded-full">
            <div
              className="h-2 transition-all duration-300 bg-green-600 rounded-full"
              style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>      {/* Questions */}
      <div className="space-y-4">
        {questions.length > 0 ? questions.map((question) => {
          const QuestionComponent = QUESTION_COMPONENTS[question.type as keyof typeof QUESTION_COMPONENTS];
          const questionValue = data?.[question.id] || {};
          const questionError = errors?.[question.id];

          return (
            <div key={question.id} className="p-4 transition-colors bg-white border border-gray-200 rounded-lg hover:border-green-300">
              <QuestionComponent
                id={question.id}
                question={`${question.id}: ${question.question}`}
                value={questionValue}
                onChange={(value) => updateQuestion(question.id, value)}
                required={question.required}
                error={questionError}
              />
            </div>
          );
        }) : (
          <div className="py-8 text-center text-gray-500">
            {selectedTimeline ? 'No questions available for this timeline.' : 'Select a timeline to view questions.'}
          </div>
        )}
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 className="mb-2 font-medium text-red-900">Please complete the following required questions:</h4>
          <ul className="space-y-1 text-sm text-red-800">
            {Object.entries(errors).map(([questionId, error]) => (
              <li key={questionId}>• {questionId}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Period Summary */}
      <div className="p-4 rounded-lg bg-gray-50">
        <h4 className="mb-2 font-medium text-gray-900">Summary</h4>
        <p className="text-sm text-gray-700">
          {questionDescription}
        </p>
        <div className="mt-2 text-sm text-gray-600">
          Completion: {completed}/{total} questions ({total > 0 ? Math.round((completed / total) * 100) : 0}%)
        </div>
      </div>

      {/* Footer with Question Counter and Progress */}
      <div className="p-4 mt-8 border border-gray-100 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <span className="font-medium">{total}</span> questions in this section
          </div>
          <div className="flex items-center text-sm">
            <span className="mr-2 font-medium text-blue-600">Question {questionNumber} of {totalSections}</span>
            <div className="flex space-x-1">
              {Array.from({ length: totalSections }, (_, i) => (
                <div
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full ${i < currentSection ? 'bg-blue-600' : 'bg-gray-300'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3Question2;